import * as React from 'react'

import { PlaceModel } from 'app/models/placeModel'

import { PlaceEntryType } from 'app/constants/placeEntryType'

import {
  getCountryFlagStrict,
  CountryCode,
} from '@/modules/shared/config/country-flag'

import { useScene } from '../context/SceneContext'

export type PlaceBlockProps = {
  place: PlaceModel
  index: number
}

const lineColor = '#E1E1EB'
const autoLineColor = '#2C57FF'

const primaryColor = '#2448BB'

const flagBorderColor = lineColor

const getFlagIcon = (countryCode: string): string | null => {
  if (!countryCode) return null
  return getCountryFlagStrict(countryCode.toUpperCase() as CountryCode) ?? null
}

const drawLeftFlag = (countryCode: string) => {
  const flagIcon = getFlagIcon(countryCode)

  if (!flagIcon) {
    return (
      <svg
        width={30}
        height={16}
        style={{ marginLeft: -28, marginBottom: -20 }}
      >
        <rect
          x={6}
          y={2}
          width={22}
          height={14}
          style={{ fill: '#fefefe', stroke: flagBorderColor, strokeWidth: 1 }}
        />
        <text
          x="9"
          y="13"
          style={{ fontSize: 11, fontWeight: 400, textAlign: 'center' }}
        >
          {countryCode}
        </text>
      </svg>
    )
  }

  return (
    <svg
      width={30}
      height={16}
      viewBox="0 0 30 16"
      style={{ marginLeft: -28, marginBottom: -20 }}
    >
      <image
        href={flagIcon}
        x={6}
        y={2}
        width={22}
        height={14}
        preserveAspectRatio="xMidYMid slice"
      />

      <rect
        x={6}
        y={2}
        width={22}
        height={14}
        fill="none"
        stroke={flagBorderColor}
        strokeWidth={1}
      />
    </svg>
  )
}

const drawRightFlag = (countryCode: string) => {
  const flagIcon = getFlagIcon(countryCode)

  if (!flagIcon) {
    return (
      <svg width={30} height={16} style={{ marginLeft: -1, marginBottom: -20 }}>
        <rect
          x={2}
          y={2}
          width={22}
          height={14}
          style={{ fill: '#fefefe', stroke: flagBorderColor, strokeWidth: 1 }}
        />
        <text x="5" y="13" style={{ fontSize: 11, fontWeight: 400 }}>
          {countryCode}
        </text>
      </svg>
    )
  }

  return (
    <svg
      width={30}
      height={16}
      viewBox="0 0 30 16"
      style={{ marginLeft: -1, marginBottom: -20 }}
    >
      <image
        href={flagIcon}
        x={2}
        y={2}
        width={22}
        height={14}
        preserveAspectRatio="xMidYMid slice"
      />

      <rect
        x={2}
        y={2}
        width={22}
        height={14}
        fill="none"
        stroke={flagBorderColor}
        strokeWidth={1}
      />
    </svg>
  )
}

export type PlaceMarkerSpan = { start: number; end: number }

export const placeMarkerSpan = (
  place: PlaceModel,
  x: number,
): PlaceMarkerSpan => {
  if (place.entryType === PlaceEntryType.END) return { start: x + 1, end: x + 23 }
  return { start: x - 22, end: x }
}

const prepareTooltipInfo = (place: PlaceModel) => {
  return {
    startTime: place.startTime,
    endTime: place.endTime,
    entryType: place.entryType,
    nation: place.nation,
    typeName: 'eventName.place',
    color: place.isGnssPosition ? primaryColor : '#171494',
  }
}

export const PlaceBlock: React.FC<PlaceBlockProps> = React.memo(
  (props: PlaceBlockProps) => {
    const scene = useScene()

    const HEIGHT = scene.height - 2

    const { place } = props
    const isAuto = place.isAutomaticBorder
    const color = isAuto
      ? autoLineColor
      : place.isGnssPosition
        ? primaryColor
        : lineColor
    const position = scene.timeToX(place.startTime)
    const fullHeight = scene.weekly
      ? scene.height - scene.headerHeight - scene.bottomPadding
      : HEIGHT - 23
    const height = fullHeight

    return (
      <div
        className="tw-flex tw-flex-col tw-items-start tw-justify-end"
        style={{
          position: 'absolute',
          left: position,
          top: 0,
          bottom: 0,
        }}
      >
        <div
          style={{ width: 1, pointerEvents: 'auto' }}
          data-tip={JSON.stringify(prepareTooltipInfo(place))}
          data-for="placeItemTooltip"
        >
          {place.entryType === PlaceEntryType.END
            ? drawRightFlag(place.nation)
            : drawLeftFlag(place.nation)}
          <div
            style={{
              background: color,
              height,
              width: 2,
              marginBottom: scene.bottomPadding,
            }}
          />
        </div>
      </div>
    )
  },
)

PlaceBlock.displayName = 'PlaceBlock'
